import { useMemo } from "react";
import { useCurrentProject } from "./useCurrentProject";

export interface GrantablePermissions {
  /** Permissions the current user has on this project. Empty for users with
   *  no membership; `isSuperadmin` short-circuits all checks below. */
  set: ReadonlySet<string>;
  /** True when the user is a superadmin — every permission is grantable. */
  isSuperadmin: boolean;
  /** True when the user has the given permission on this project (or is a
   *  superadmin). */
  has: (key: string) => boolean;
  /** Permission keys present on the role the current user lacks. Returns
   *  `[]` for superadmins. */
  unsatisfiable: (rolePermissions: readonly string[]) => string[];
  /** True when the user has every permission the role grants. */
  canGrantRole: (rolePermissions: readonly string[]) => boolean;
}

/**
 * Privilege-escalation guard. Admins shouldn't be able to grant another user
 * a permission they don't have themselves — this hook centralises that check
 * so the role-edit grid, invite dialog, and member role-change dropdown stay
 * in lockstep.
 *
 * The data source is `useCurrentProject().role.permissions`, which is the
 * live list returned by `/auth/me` (mirrored into the auth store), so it
 * reflects custom roles too — not just the static system trio.
 */
export function useGrantablePermissions(): GrantablePermissions {
  const { role, isSuperadmin } = useCurrentProject();
  return useMemo<GrantablePermissions>(() => {
    const set: ReadonlySet<string> = new Set(role?.permissions ?? []);
    return {
      set,
      isSuperadmin,
      has: (k) => isSuperadmin || set.has(k),
      unsatisfiable: (rp) =>
        isSuperadmin ? [] : rp.filter((p) => !set.has(p)),
      canGrantRole: (rp) => isSuperadmin || rp.every((p) => set.has(p)),
    };
  }, [role, isSuperadmin]);
}
